import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import axios from '../store/axiosInterceptor';
import { DetailPageSkeleton } from '../components/Skeleton';
import '../styles/quill-custom.css';

interface Coach {
  id: string;
  name: string;
  email: string;
  phone: string;
  first_name: string;
  last_name: string;
  status: string;
  area_of_expertise: string;
  completed_sessions: number;
  avatar: string;
  bio: string;
  notes: string;
  location: string;
  timezone: string;
  approved: boolean;
  averageRating?: number;
}

interface AssignedStudent {
  id: string;
  name: string;
  avatar: string;
  focusArea: string;
  sessions: number;
}

const CoachDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [coach, setCoach] = useState<Coach | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'students' | 'sessions' | 'reviews' | 'notes'>('students');
  const [noteContent, setNoteContent] = useState('');
  const [showNoteEditor, setShowNoteEditor] = useState(false);
  const [assignedStudents, setAssignedStudents] = useState<AssignedStudent[]>([]);
  const [sessions, setSessions] = useState<any[]>([]);
  const [reviews, setReviews] = useState<any[]>([]);
  const [notes, setNotes] = useState<Array<{ id: string; date: string; content: string }>>([]);

  // Fetch coach details from API
  useEffect(() => {
    const fetchCoachDetails = async () => {
      try {
        if (!id) return;
        const { data } = await axios.get(`/api/coaches/${id}`);
        setCoach(data.coach);
        
        // Transform students data to match interface
        const transformedStudents = (data.students || []).map((student: any) => ({
          id: student.user_id,
          name: student.name,
          avatar: student.avatar,
          focusArea: student.focus_area,
          sessions: student.total_sessions
        }));
        
        setAssignedStudents(transformedStudents);
        setSessions(data.sessions || []);
        setReviews(data.reviews || []);
        setNotes(data.notes || []);
      } catch (error) {
        console.error('Error fetching coach details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCoachDetails();
  }, [id]);

  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    
    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(
          <svg key={i} className="h-5 w-5 fill-orange-400" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        );
      } else {
        stars.push(
          <svg key={i} className="h-5 w-5 fill-gray-300" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        );
      }
    }
    
    return stars;
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
    ],
    content: noteContent,
    onUpdate: ({ editor }) => {
      setNoteContent(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none',
        'data-placeholder': 'Write your note here',
      },
    },
  });

  const handleAddNote = async () => {
    if (noteContent.trim() && noteContent !== '<p></p>') {
      const newNote = {
        id: Date.now().toString(),
        date: new Date().toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric', 
          year: 'numeric' 
        }),
        content: noteContent,
      };
      
      // Add to local state immediately
      setNotes([newNote, ...notes]);
      setNoteContent('');
      editor?.commands.clearContent();
      setShowNoteEditor(false);

      // Save to backend
      try {
        await axios.post(`/api/admin/coaches/${id}/notes`, {
          content: noteContent,
          date: new Date().toISOString(),
        });
      } catch (error) {
        console.error('Error adding note:', error);
      }
    }
  };

  const handleCancelNote = () => {
    setNoteContent('');
    editor?.commands.clearContent();
    setShowNoteEditor(false);
  };

  if (loading) {
    return <DetailPageSkeleton />;
  }

  if (!coach) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-gray-500">Coach not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Coach Profile Card */}
      <div className="rounded-xl border border-[#F2F4F7] bg-white p-6 shadow-[0_4px_12px_0_rgba(249,250,251,1)]">
        {/* Back Button */}
        <button
          onClick={() => navigate('/coaches')}
          className="mb-6 flex items-center gap-2 text-sm font-medium text-gray-900 hover:text-gray-700"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span>Coach Details</span>
        </button>

        {/* Top Section */}
        <div className="mb-6 flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 rounded-full bg-blue-500 flex items-center justify-center">
              <span className="text-2xl font-semibold text-white">
                {coach.first_name && coach.last_name 
                  ? `${coach.first_name[0]}${coach.last_name[0]}` 
                  : coach.name 
                    ? coach.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
                    : 'C'}
              </span>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {coach.first_name && coach.last_name 
                  ? `${coach.first_name} ${coach.last_name}` 
                  : coach.name || 'Coach'}
              </h2>
              <div className="mt-1 flex items-center gap-4 text-sm text-gray-500">
                <span>{coach.email}</span>
                <div className="flex items-center gap-1">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                  <span>{coach.phone}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`rounded-[7px] px-4 py-1.5 text-sm font-medium ${
              coach.approved 
                ? 'bg-green-100 text-green-700' 
                : 'bg-orange-100 text-orange-600'
            }`}>
              {coach.approved ? 'Active' : 'Pending Activation'}
            </span>
          </div>
        </div>

        {/* Stats Grid */}
        <div className={`grid gap-6 border-t border-[#F2F4F7] pt-6 ${coach?.averageRating != null ? 'grid-cols-3' : 'grid-cols-2'}`}>
          <div>
            <p className="text-xs text-gray-400 mb-1">Area of Expertise</p>
            <p className="font-medium text-gray-900 truncate" title={coach.area_of_expertise}>
              {coach.area_of_expertise}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-1">Completed Sessions</p>
            <p className="font-medium text-gray-900">{coach.completed_sessions}</p>
          </div>
          {coach?.averageRating != null && (
            <div>
              <p className="text-xs text-gray-400 mb-1">Average Rating</p>
              <div className="flex items-center gap-2">
                <div className="flex gap-0.5">
                  {renderStars(coach.averageRating)}
                </div>
                <span className="font-medium text-gray-900">{coach.averageRating.toFixed(1)}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-8 border-b border-[#F2F4F7]">
        <button
          onClick={() => setActiveTab('students')}
          className={`pb-3 text-sm font-medium transition-colors ${
            activeTab === 'students'
              ? 'border-b-2 border-gray-900 text-gray-900'
              : 'text-gray-500 hover:text-gray-900'
          }`}
        >
          Assigned Students ({assignedStudents.length})
        </button>
        <button
          onClick={() => setActiveTab('sessions')}
          className={`pb-3 text-sm font-medium transition-colors ${
            activeTab === 'sessions'
              ? 'border-b-2 border-gray-900 text-gray-900'
              : 'text-gray-500 hover:text-gray-900'
          }`}
        >
          Sessions
        </button>
        <button
          onClick={() => setActiveTab('reviews')}
          className={`pb-3 text-sm font-medium transition-colors ${
            activeTab === 'reviews'
              ? 'border-b-2 border-gray-900 text-gray-900'
              : 'text-gray-500 hover:text-gray-900'
          }`}
        >
          Reviews
        </button>
        <button
          onClick={() => setActiveTab('notes')}
          className={`pb-3 text-sm font-medium transition-colors ${
            activeTab === 'notes'
              ? 'border-b-2 border-gray-900 text-gray-900'
              : 'text-gray-500 hover:text-gray-900'
          }`}
        >
          Notes
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'students' && (
        <div className="space-y-3">
          {assignedStudents.length > 0 ? (
            assignedStudents.map((student) => (
              <div
                key={student.id}
                className="flex items-center justify-between rounded-xl border border-[#F2F4F7] bg-white p-4 shadow-[0_4px_12px_0_rgba(249,250,251,1)] hover:shadow-md transition-shadow"
              >
                <div className="flex items-center gap-3">
                  {student.avatar ? (
                    <img 
                      src={student.avatar} 
                      alt={student.name}
                      className="h-12 w-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="h-12 w-12 rounded-full bg-blue-500 flex items-center justify-center">
                      <span className="text-lg font-semibold text-white">
                        {student.name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div>
                    <p className="font-medium text-gray-900">{student.name}</p>
                    <p className="text-sm text-gray-500">{student.focusArea}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-400 mb-1">Sessions</p>
                  <p className="font-semibold text-gray-900">{student.sessions}</p>
                </div>
              </div>
            ))
          ) : (
            <div className="rounded-xl border border-[#F2F4F7] bg-white p-6 shadow-[0_4px_12px_0_rgba(249,250,251,1)]">
              <p className="text-gray-500 text-center py-8">No students assigned yet</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'sessions' && (
        <div className="space-y-4">
          {sessions.length > 0 ? (
            sessions.map((session) => (
              <div
                key={session.id}
                className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-6 py-5"
              >
                <div className="flex items-center gap-6">
                  <div className="min-w-[140px]">
                    <div className="text-sm font-medium text-gray-900">
                      {session.date_string || 'N/A'}
                    </div>
                  </div>
                  <div className="min-w-[80px] text-sm text-gray-500">{session.time || 'N/A'}</div>
                  <div className="flex items-center gap-3">
                    {session.student_avatar ? (
                      <img 
                        src={session.student_avatar} 
                        alt={session.student_name}
                        className="h-10 w-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-gradient-to-br from-purple-400 to-pink-600 flex items-center justify-center">
                        <span className="text-sm font-semibold text-white">
                          {session.student_name ? session.student_name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase() : 'S'}
                        </span>
                      </div>
                    )}
                    <span className="text-sm font-medium text-gray-900">{session.student_name || 'Unknown'}</span>
                  </div>
                </div>
                <span
                  className={`rounded-md px-3 py-1.5 text-xs font-medium ${
                    session.status === 'upcoming'
                      ? 'bg-orange-100 text-orange-700'
                      : session.status === 'completed'
                      ? 'bg-green-100 text-green-700'
                      : session.status === 'missed'
                      ? 'bg-red-100 text-red-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {session.status}
                </span>
              </div>
            ))
          ) : (
            <div className="rounded-xl border border-[#F2F4F7] bg-white p-6 shadow-[0_4px_12px_0_rgba(249,250,251,1)]">
              <p className="text-gray-500 text-center py-8">No sessions yet</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'reviews' && (
        <div className="grid grid-cols-2 gap-6">
          {reviews.length > 0 ? (
            reviews.map((review) => (
              <div key={review.id} className="rounded-lg border border-gray-200 bg-white p-6">
                <div className="mb-4 flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    {review.student_avatar ? (
                      <img 
                        src={review.student_avatar} 
                        alt={review.student_name || review.student}
                        className="h-12 w-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-400 to-purple-600 flex items-center justify-center">
                        <span className="text-lg font-semibold text-white">
                          {(review.student_name || review.student || 'S').split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div>
                      <p className="font-semibold text-gray-900">{review.student_name || review.student || 'Unknown'}</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400">
                    Reviewed: {review.date_string || new Date(review.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </p>
                </div>
                <div className="mb-4 flex items-center gap-2">
                  <div className="flex gap-0.5">{renderStars(review.rating)}</div>
                  <span className="text-sm font-semibold text-gray-900">{review.rating}</span>
                </div>
                <p className="text-sm leading-relaxed text-gray-600">{review.comment}</p>
              </div>
            ))
          ) : (
            <div className="col-span-2 rounded-xl border border-[#F2F4F7] bg-white p-6 shadow-[0_4px_12px_0_rgba(249,250,251,1)]">
              <p className="text-gray-500 text-center py-8">No reviews yet</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'notes' && (
        <div className="flex flex-col gap-4">
          {/* Add Button */}
          {!showNoteEditor && (
            <button
              type="button"
              className="w-fit rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:border-gray-300 hover:text-gray-900"
              onClick={() => setShowNoteEditor(true)}
            >
              <span className="mr-2 text-lg leading-none">+</span>
              Add
            </button>
          )}

          {/* No notes message */}
          {!showNoteEditor && notes.length === 0 && (
            <p className="text-sm text-gray-500">No notes yet.</p>
          )}

          {/* Note Editor */}
          {showNoteEditor && (
            <div className="rounded-[18px] border border-gray-100 bg-white p-4 shadow-sm">
              <div className="rounded-[16px] bg-gray-50 border border-gray-200">
                <EditorContent 
                  editor={editor} 
                  className="prose prose-sm max-w-none min-h-[11rem] px-3 py-2"
                />
              </div>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => editor?.chain().focus().toggleBold().run()}
                    className={`flex h-9 w-9 items-center justify-center rounded text-base font-bold transition-colors ${
                      editor?.isActive('bold')
                        ? 'bg-gray-900 text-white'
                        : 'bg-[rgba(228,231,236,0.2)] text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    B
                  </button>
                  <button
                    type="button"
                    onClick={() => editor?.chain().focus().toggleItalic().run()}
                    className={`flex h-9 w-9 items-center justify-center rounded text-base italic transition-colors ${
                      editor?.isActive('italic')
                        ? 'bg-gray-900 text-white'
                        : 'bg-[rgba(228,231,236,0.2)] text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    I
                  </button>
                  <button
                    type="button"
                    onClick={() => editor?.chain().focus().toggleUnderline().run()}
                    className={`flex h-9 w-9 items-center justify-center rounded text-base underline transition-colors ${
                      editor?.isActive('underline')
                        ? 'bg-gray-900 text-white'
                        : 'bg-[rgba(228,231,236,0.2)] text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    U
                  </button>
                  <span className="h-6 w-px bg-gray-200" />
                  <button
                    type="button"
                    onClick={() => editor?.chain().focus().toggleBulletList().run()}
                    className={`flex h-9 w-9 items-center justify-center rounded text-lg transition-colors ${
                      editor?.isActive('bulletList')
                        ? 'bg-gray-900 text-white'
                        : 'bg-[rgba(228,231,236,0.2)] text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    •
                  </button>
                  <button
                    type="button"
                    onClick={() => editor?.chain().focus().setTextAlign('left').run()}
                    className={`flex h-9 w-9 items-center justify-center rounded text-base transition-colors ${
                      editor?.isActive({ textAlign: 'left' })
                        ? 'bg-gray-900 text-white'
                        : 'bg-[rgba(228,231,236,0.2)] text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    ≡
                  </button>
                  <button
                    type="button"
                    onClick={() => editor?.chain().focus().setTextAlign('center').run()}
                    className={`flex h-9 w-9 items-center justify-center rounded text-base transition-colors ${
                      editor?.isActive({ textAlign: 'center' })
                        ? 'bg-gray-900 text-white'
                        : 'bg-[rgba(228,231,236,0.2)] text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    ≣
                  </button>
                  <button
                    type="button"
                    onClick={() => editor?.chain().focus().setTextAlign('right').run()}
                    className={`flex h-9 w-9 items-center justify-center rounded text-base transition-colors ${
                      editor?.isActive({ textAlign: 'right' })
                        ? 'bg-gray-900 text-white'
                        : 'bg-[rgba(228,231,236,0.2)] text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    ≡
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const url = window.prompt('Enter URL:');
                      if (url) {
                        editor?.chain().focus().setLink({ href: url }).run();
                      }
                    }}
                    className={`flex h-9 w-9 items-center justify-center rounded text-lg transition-colors ${
                      editor?.isActive('link')
                        ? 'bg-gray-900 text-white'
                        : 'bg-[rgba(228,231,236,0.2)] text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    🔗
                  </button>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:border-gray-300 hover:text-gray-900"
                    onClick={handleCancelNote}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={`rounded-xl px-4 py-2 text-sm font-semibold text-white transition ${
                      !noteContent.trim() || noteContent === '<p></p>'
                        ? 'bg-gray-200 cursor-not-allowed'
                        : 'bg-gray-900 hover:bg-gray-800'
                    }`}
                    disabled={!noteContent.trim() || noteContent === '<p></p>'}
                    onClick={handleAddNote}
                  >
                    Post
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Notes List */}
          {notes.length > 0 && (
            <div className="flex flex-col gap-6">
              {notes.map((note) => (
                <div key={note.id} className="grid grid-cols-[110px_1fr] gap-4">
                  <span className="text-xs font-semibold text-gray-400">
                    {new Date(note.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                  <div className="markdown-data text-sm leading-relaxed font-medium text-[#0F0F0F]">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeRaw]}
                    >
                      {note.content}
                    </ReactMarkdown>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CoachDetailsPage;
